import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Typography } from '@mui/material';
import { TrackerDataType } from '../../shared/constants/constants';
import CalendarSnapshotChip from './CalendarSnapshotChip';
import CalendarTypeChip from './CalendarTypeChip';
import { CalendarEvent } from './types';
import { calendarEntryCardPalette, calendarStatusPalette, calendarUiPalette } from './calendarPalette';

interface CalendarSelectedDayEntryCardProps {
  event: CalendarEvent;
  onClick: (event: CalendarEvent) => void;
}

type CalendarEventLogType = CalendarEvent['logtype'];

const categoryLabelMap = {
  [TrackerDataType.SERVICE]: 'Service Log',
  [TrackerDataType.MEDIA_INTERACTION]: 'Media Interaction',
  [TrackerDataType.ENGAGEMENT]: 'Engagement',
  [TrackerDataType.CONTENT_LOG]: 'Content Log',
} as const satisfies Record<CalendarEventLogType, string>;

const getCategoryLabel = (logtype: CalendarEventLogType) => categoryLabelMap[logtype];

const truncateMetaValue = (value: string) => {
  if (value.length <= 50) {
    return value;
  }

  return `${value.slice(0, 50)}...`;
};

const joinNames = (items?: Array<{ name: string }>) =>
  (items ?? [])
    .map((item) => item.name?.trim())
    .filter(Boolean)
    .join(', ');

function getSelectedDayCardMetadata(event: CalendarEvent) {
  switch (event.logtype) {
    case TrackerDataType.SERVICE: {
      const primary = event.campaign || '';
      const secondary = primary ? '' : event.unit || '';
      return [primary, secondary].filter(Boolean).join(' • ') || getCategoryLabel(event.logtype);
    }
    case TrackerDataType.MEDIA_INTERACTION: {
      const primary = event.outlet || '';
      const secondary = event.journalist || '';
      return [primary, secondary].filter(Boolean).join(' • ') || getCategoryLabel(event.logtype);
    }
    case TrackerDataType.ENGAGEMENT: {
      const primary = event.organization || '';
      const secondary = primary ? '' : event.communityMember || '';
      return [primary, secondary].filter(Boolean).join(' • ') || getCategoryLabel(event.logtype);
    }
    case TrackerDataType.CONTENT_LOG: {
      const linkedServiceNames = joinNames(event.linkedServiceItems);
      const audienceNames = joinNames(event.audienceItems);
      const primary = linkedServiceNames || '';
      const secondary = audienceNames || event.primaryTitle || '';
      return [primary, secondary].filter(Boolean).join(' • ') || getCategoryLabel(event.logtype);
    }
    default:
      return 'Service Log';
  }
}

export default function CalendarSelectedDayEntryCard({ event, onClick }: Readonly<CalendarSelectedDayEntryCardProps>) {
  const palette = calendarEntryCardPalette[event.logtype] ?? calendarEntryCardPalette[TrackerDataType.SERVICE];
  const statusPalette = calendarStatusPalette[event.status] ?? calendarStatusPalette.default;
  const secondaryMetaValue = getSelectedDayCardMetadata(event);
  const secondaryMetaDisplay = truncateMetaValue(secondaryMetaValue);
  const visibleTypeChip = event.typeItems?.[0];
  const hiddenTypeCount = Math.max((event.typeItems?.length ?? 0) - 1, 0);

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onClick(event)}
      data-cy={`calendar-selected-day-entry-card-${event.logtype}-${event.id}`}
      sx={{
        display: 'flex',
        position: 'relative',
        alignItems: 'stretch',
        width: '100%',
        minHeight: '118px',
        overflow: 'hidden',
        p: 0,
        boxSizing: 'border-box',
        border: `1px solid ${calendarUiPalette.selectedDayPanel.entryCardBorder}`,
        borderRadius: '12px',
        backgroundColor: calendarUiPalette.selectedDayPanel.entryCardBackground,
        boxShadow: calendarUiPalette.selectedDayPanel.entryCardShadow,
        textAlign: 'left',
        cursor: 'pointer',
        appearance: 'none',
        transition: 'transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          borderColor: palette.borderColor,
          boxShadow: calendarUiPalette.selectedDayPanel.entryCardHoverShadow,
        },
        '&:hover .calendar-entry-card-snapshot-action': {
          color: palette.borderColor,
        },
        '&:hover .calendar-entry-card-snapshot-label': {
          textDecoration: 'underline',
          textDecorationColor: palette.borderColor,
        },
      }}
    >
      <Box
        component="span"
        sx={{
          minHeight: '100%',
          width: '4px',
          flexShrink: 0,
          borderRadius: '12px 0 0 12px',
          backgroundColor: palette.borderColor,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.4,
          minWidth: 0,
          flex: 1,
          px: 1.75,
          py: 1.35,
        }}
      >
        <Typography
          sx={{
            color: palette.eyebrowColor,
            fontSize: '0.62rem',
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            mb: 0.2,
          }}
        >
          {getCategoryLabel(event.logtype)}
        </Typography>
        <Typography
          sx={{
            width: '100%',
            overflow: 'hidden',
            color: palette.titleColor,
            fontSize: '17px',
            fontWeight: 700,
            lineHeight: 1.15,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={event.primaryTitle}
        >
          {event.primaryTitle}
        </Typography>
        <Box
          sx={{
            minWidth: 0,
            color: palette.metaColor,
            width: '100%',
            mb: 0.2,
          }}
        >
          <Typography
            sx={{
              overflow: 'hidden',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: 1.25,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={secondaryMetaValue}
          >
            {secondaryMetaDisplay}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            width: '100%',
            mt: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
            <CalendarSnapshotChip
              label={event.status}
              backgroundColor={statusPalette.backgroundColor}
              borderColor={statusPalette.backgroundColor}
              color={statusPalette.color}
              sx={{
                minHeight: '23px',
                px: 0.95,
                borderRadius: '999px',
              }}
            />
            {visibleTypeChip ? (
              <CalendarTypeChip
                name={visibleTypeChip.name}
                backgroundColor={visibleTypeChip.backgroundColor}
                sx={{
                  '&.MuiChip-root': {
                    height: '23px',
                    borderRadius: '999px',
                  },
                  '& .MuiChip-label': {
                    px: 0.95,
                  },
                }}
              />
            ) : null}
            {hiddenTypeCount > 0 ? (
              <CalendarSnapshotChip
                label={`+${hiddenTypeCount} more`}
                backgroundColor={calendarUiPalette.selectedDayPanel.entryCardDateChipBackground}
                borderColor={calendarUiPalette.eventModal.outlinedChipBorder}
                color={calendarUiPalette.selectedDayPanel.entryCardDateChipText}
                sx={{
                  minHeight: '23px',
                  px: 0.95,
                  borderRadius: '999px',
                }}
              />
            ) : null}
          </Box>
          <Box
            component="span"
            className="calendar-entry-card-snapshot-action"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.1,
              flexShrink: 0,
              color: calendarUiPalette.selectedDayPanel.entryCardActionText,
              fontSize: '0.72rem',
              fontWeight: 600,
              lineHeight: 1.2,
              transition: 'color 0.16s ease',
            }}
          >
            <Box
              component="span"
              className="calendar-entry-card-snapshot-label"
              sx={{
                textDecoration: 'none',
                textUnderlineOffset: '0.14em',
              }}
            >
              View snapshot
            </Box>
            <ChevronRightIcon sx={{ fontSize: 14 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
